import { useState } from 'react';
import { ArrowLeft, Star, Crown, Sparkles, MessageCircle, Eye, Heart, TrendingUp, Shield, Zap, Check, Video, Camera, FileText, Users, Phone, Gift, CheckCircle, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Separator } from './ui/separator';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';

interface PremiumMarketplaceProps {
  onBack: () => void;
}

interface PremiumFeature {
  id: string;
  title: string;
  description: string;
  price: number;
  icon: any;
  category: 'boost' | 'unlock' | 'premium' | 'verification' | 'services';
  color: string;
  popular?: boolean;
  features: string[];
  duration?: string;
}

export default function PremiumMarketplace({ onBack }: PremiumMarketplaceProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedFeature, setSelectedFeature] = useState<PremiumFeature | null>(null);

  const premiumFeatures: PremiumFeature[] = [
    {
      id: 'profile-boost-1day',
      title: 'Profile Boost - 1 Day',
      description: 'Get 10x more profile views for 24 hours',
      price: 299,
      icon: Zap,
      category: 'boost',
      color: 'bg-yellow-500',
      popular: true,
      duration: '24 hours',
      features: [
        'Appear at top of search results',
        '10x more profile visibility',
        'Priority in matches',
        'Instant activation'
      ]
    },
    {
      id: 'profile-boost-3day',
      title: 'Profile Boost - 3 Days',
      description: 'Extended visibility for 3 days',
      price: 699,
      icon: Zap,
      category: 'boost',
      color: 'bg-yellow-500',
      duration: '3 days',
      features: [
        'Top position for 3 days',
        '10x visibility throughout',
        'Best value for visibility',
        'Instant activation'
      ]
    },
    {
      id: 'profile-boost-7day',
      title: 'Profile Boost - 1 Week',
      description: 'Maximum exposure for a full week',
      price: 1299,
      icon: Zap,
      category: 'boost',
      color: 'bg-yellow-500',
      duration: '7 days',
      features: [
        'Top position for 1 week',
        'Maximum profile reach',
        'Save 30% vs daily',
        'Instant activation'
      ]
    },
    {
      id: 'super-like-10',
      title: 'Super Likes - Pack of 10',
      description: 'Stand out with special interest notifications',
      price: 199,
      icon: Heart,
      category: 'boost',
      color: 'bg-pink-500',
      features: [
        '10 Super Likes',
        'Priority notification to recipient',
        '5x higher response rate',
        'Valid for 30 days'
      ]
    },
    {
      id: 'super-like-50',
      title: 'Super Likes - Pack of 50',
      description: 'Best value super like package',
      price: 799,
      icon: Heart,
      category: 'boost',
      color: 'bg-pink-500',
      popular: true,
      features: [
        '50 Super Likes',
        'Save 20% vs pack of 10',
        'Priority notifications',
        'Valid for 60 days'
      ]
    },
    {
      id: 'read-receipts',
      title: 'Read Receipts',
      description: 'Know when your messages are read',
      price: 499,
      icon: Eye,
      category: 'unlock',
      color: 'bg-blue-500',
      duration: '1 month',
      features: [
        'See message read status',
        'Track engagement',
        'Better communication',
        'Auto-renews monthly'
      ]
    },
    {
      id: 'unlimited-messaging',
      title: 'Unlimited Messaging',
      description: 'Chat with unlimited matches',
      price: 999,
      icon: MessageCircle,
      category: 'unlock',
      color: 'bg-green-500',
      duration: '1 month',
      features: [
        'Unlimited chat access',
        'Message anyone',
        'No daily limits',
        'Premium support'
      ]
    },
    {
      id: 'horoscope-matching',
      title: 'Horoscope Matching',
      description: 'Get detailed kundali compatibility reports',
      price: 599,
      icon: Star,
      category: 'services',
      color: 'bg-purple-500',
      popular: true,
      features: [
        'Detailed Gun Milan report',
        'Expert astrologer analysis',
        'Compatibility score',
        'Recommendations included'
      ]
    },
    {
      id: 'video-calling',
      title: 'Video Calling - Unlimited',
      description: 'Connect via secure video calls',
      price: 799,
      icon: Video,
      category: 'unlock',
      color: 'bg-red-500',
      duration: '1 month',
      features: [
        'Unlimited video calls',
        'HD quality',
        'Secure & private',
        'Record consent feature'
      ]
    },
    {
      id: 'govt-id-verification',
      title: 'Government ID Verification',
      description: 'Verify your profile with official ID',
      price: 199,
      icon: Shield,
      category: 'verification',
      color: 'bg-teal-500',
      features: [
        'Verified badge on profile',
        '3x more trust',
        'Higher response rates',
        'One-time verification'
      ]
    },
    {
      id: 'photo-verification',
      title: 'Photo Verification',
      description: 'Prove your photos are authentic',
      price: 99,
      icon: Camera,
      category: 'verification',
      color: 'bg-teal-500',
      features: [
        'Photo verified badge',
        'Instant verification',
        'Builds trust',
        'One-time fee'
      ]
    },
    {
      id: 'background-check',
      title: 'Background Verification',
      description: 'Professional background screening',
      price: 2999,
      icon: FileText,
      category: 'verification',
      color: 'bg-teal-500',
      features: [
        'Employment verification',
        'Education verification',
        'Criminal record check',
        'Detailed report in 7 days'
      ]
    },
    {
      id: 'profile-writer',
      title: 'Professional Profile Writing',
      description: 'Expert-written profile to attract matches',
      price: 1499,
      icon: FileText,
      category: 'services',
      color: 'bg-indigo-500',
      features: [
        'Written by dating experts',
        'Optimized for matches',
        '2 revisions included',
        'Delivered in 48 hours'
      ]
    },
    {
      id: 'matchmaker-service',
      title: 'Personal Matchmaker',
      description: 'Dedicated relationship manager',
      price: 4999,
      icon: Users,
      category: 'services',
      color: 'bg-indigo-500',
      duration: '1 month',
      features: [
        'Dedicated matchmaker',
        'Hand-picked matches',
        'Profile optimization',
        'Dating coaching included'
      ]
    },
    {
      id: 'photo-shoot',
      title: 'Professional Photo Shoot',
      description: 'Professional photography session',
      price: 3999,
      icon: Camera,
      category: 'services',
      color: 'bg-indigo-500',
      popular: true,
      features: [
        '1-hour photo session',
        '20+ edited photos',
        'Professional photographer',
        'Available in major cities'
      ]
    },
    {
      id: 'priority-support',
      title: 'Priority Customer Support',
      description: '24/7 dedicated support line',
      price: 599,
      icon: Phone,
      category: 'services',
      color: 'bg-orange-500',
      duration: '1 month',
      features: [
        '24/7 phone support',
        'Dedicated support agent',
        'Response within 1 hour',
        'Profile assistance'
      ]
    },
    {
      id: 'virtual-gifts',
      title: 'Virtual Gifts Bundle',
      description: 'Send special gifts to your matches',
      price: 499,
      icon: Gift,
      category: 'boost',
      color: 'bg-pink-500',
      features: [
        '20 virtual gifts',
        'Unique animations',
        'Express interest creatively',
        'Never expires'
      ]
    },
    {
      id: 'compatibility-report',
      title: 'AI Compatibility Report',
      description: 'Deep analysis of match compatibility',
      price: 399,
      icon: Sparkles,
      category: 'services',
      color: 'bg-purple-500',
      features: [
        'AI-powered analysis',
        'Personality matching',
        'Communication tips',
        'Relationship predictions'
      ]
    }
  ];

  const categories = [
    { id: 'all', label: 'All Features', icon: Sparkles },
    { id: 'boost', label: 'Boost & Visibility', icon: Zap },
    { id: 'unlock', label: 'Unlock Features', icon: Lock },
    { id: 'verification', label: 'Verification', icon: Shield },
    { id: 'services', label: 'Premium Services', icon: Crown },
  ];

  const filteredFeatures = selectedCategory === 'all'
    ? premiumFeatures
    : premiumFeatures.filter(f => f.category === selectedCategory);

  const handlePurchase = (feature: PremiumFeature) => {
    setSelectedFeature(feature);
  };

  const handleConfirmPurchase = () => {
    if (selectedFeature) {
      // Note: Full Razorpay integration requires backend deployment with live credentials
      // For demo purposes, showing success message
      toast.info('Payment integration ready - requires backend deployment with Razorpay credentials');
      toast.success(`${selectedFeature.title} will be activated after successful payment`);
      setSelectedFeature(null);
      
      // TODO: Implement actual Razorpay checkout when backend is deployed
      // const options = {
      //   key: 'RAZORPAY_KEY_ID',
      //   amount: Math.round(selectedFeature.price * 1.18) * 100,
      //   currency: 'INR',
      //   name: 'Atootbandan',
      //   description: selectedFeature.title,
      //   handler: function (response) {
      //     // Verify payment with backend
      //   }
      // };
      // const rzp = new window.Razorpay(options);
      // rzp.open();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header */}
      <div 
        className="sticky top-0 z-50 border-b shadow-sm"
        style={{ 
          background: 'linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)',
          borderBottomColor: '#ff7e5f' 
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-semibold text-white">Premium Marketplace</h1>
                <p className="text-xs text-white/80">Boost your chances of finding love</p>
              </div>
            </div>
            <Badge className="bg-white/20 text-white border-white/30">
              <Crown className="h-3 w-3 mr-1" />
              Exclusive
            </Badge>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Featured Banner */}
        <Card className="mb-6 overflow-hidden" style={{ background: `linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)` }}>
          <CardContent className="py-8 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="h-6 w-6" />
                  <Badge className="bg-white/20 text-white border-white/30">Limited Offer</Badge>
                </div>
                <h2 className="text-2xl font-bold mb-2">Premium Members Get 5x More Matches!</h2>
                <p className="opacity-90 mb-4">Upgrade to unlock exclusive features and find your soulmate faster</p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    <span className="text-sm">Unlimited Messaging</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    <span className="text-sm">Profile Boost</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    <span className="text-sm">Priority Support</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category Filter */}
        <div className="mb-6 overflow-x-auto">
          <div className="flex gap-2 pb-2">
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  style={selectedCategory === cat.id ? { backgroundColor: '#ff7e5f' } : {}}
                  className="whitespace-nowrap"
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {cat.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {filteredFeatures.map(feature => {
            const Icon = feature.icon;
            return (
              <Card key={feature.id} className={`relative hover:shadow-xl transition-shadow ${feature.popular ? 'border-2' : ''}`} style={feature.popular ? { borderColor: '#ff7e5f' } : {}}>
                {feature.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0">
                      ⭐ Popular
                    </Badge>
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-start justify-between mb-3">
                    <div className={`${feature.color} p-3 rounded-lg text-white`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    {feature.duration && (
                      <Badge variant="outline" className="text-xs">
                        {feature.duration}
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    {feature.features.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{item}</span>
                      </div>
                    ))}
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold">₹{feature.price}</p>
                      {feature.duration && (
                        <p className="text-xs text-gray-500">per {feature.duration}</p>
                      )}
                    </div>
                    <Button 
                      onClick={() => handlePurchase(feature)}
                      style={{ backgroundColor: '#ff7e5f' }}
                    >
                      Purchase
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <Card className="mt-8" style={{ background: 'linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)' }}>
          <CardContent className="py-8">
            <div className="text-center text-white">
              <Crown className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Want Everything?</h3>
              <p className="mb-6 opacity-90 max-w-2xl mx-auto">
                Get all premium features with our Platinum plan and save up to 60%
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary">
                  View Platinum Plan
                </Button>
                <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                  Compare All Plans
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Purchase Confirmation Dialog */}
      {selectedFeature && (
        <Dialog open={!!selectedFeature} onOpenChange={() => setSelectedFeature(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Purchase</DialogTitle>
              <DialogDescription>
                You're about to purchase {selectedFeature.title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="flex items-start gap-3 mb-3">
                  {(() => {
                    const Icon = selectedFeature.icon;
                    return (
                      <div className={`${selectedFeature.color} p-2 rounded-lg text-white`}>
                        <Icon className="h-5 w-5" />
                      </div>
                    );
                  })()}
                  <div className="flex-1">
                    <h4 className="font-semibold">{selectedFeature.title}</h4>
                    <p className="text-sm text-gray-600">{selectedFeature.description}</p>
                  </div>
                </div>
                <Separator className="my-3" />
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Price</span>
                    <span className="font-semibold">₹{selectedFeature.price}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">GST (18%)</span>
                    <span className="font-semibold">₹{Math.round(selectedFeature.price * 0.18)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="font-bold text-lg">₹{Math.round(selectedFeature.price * 1.18)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Payment Method</Label>
                <RadioGroup defaultValue="card">
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="card" id="card" />
                    <Label htmlFor="card" className="flex-1 cursor-pointer">Credit/Debit Card</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="upi" id="upi" />
                    <Label htmlFor="upi" className="flex-1 cursor-pointer">UPI</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="wallet" id="wallet" />
                    <Label htmlFor="wallet" className="flex-1 cursor-pointer">Wallet</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedFeature(null)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={handleConfirmPurchase}
                  style={{ backgroundColor: '#ff7e5f' }}
                >
                  Pay ₹{Math.round(selectedFeature.price * 1.18)}
                </Button>
              </div>

              <p className="text-xs text-center text-gray-500">
                Secure payment powered by Razorpay. Your data is encrypted.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}